import { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useParams, useOutletContext } from "react-router-dom";
import { useApolloClient, useMutation } from "@apollo/client";
import { ActivityOutletContext } from "../_layout";
import { ParticipantRow } from "../../../types";
import ParticipantsTable from "../../../_components/ParticipantsTable";
import uniqBy from "lodash/uniqBy";
import AddParticipantsModal from "../add-participants/_components/AddParticipantsModal";
import { useNavigate } from "react-router-dom";
import { EXPORT_ACTIVITY_PARTICIPATIONS, GET_PARTICIPANTS } from "../../../../queries";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";

const STAGED_IMPORT_ID_KEY = (activityId: string) => `stagedImportId_${activityId}`;

export default function ActivityInstanceParticipantsPage() {
  const { activityTypeId = "", activityId = "" } = useParams();
  const { activityData } = useOutletContext<ActivityOutletContext>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const canAddParticipants = useActivityTypePermissions(
    activityData?.activityType || {},
    activityData?.managingSection?.id
  );

  const [exportActivityParticipations, { loading: exportLoading }] = useMutation(EXPORT_ACTIVITY_PARTICIPATIONS);
  const [addParticipantsModalOpen, setAddParticipantsModalOpen] = useState(false);

  const [stagedImportId, setStagedImportIdState] = useState<string | null>(null);

  useEffect(() => {
    if (activityId) {
      try {
        const stored = localStorage.getItem(STAGED_IMPORT_ID_KEY(activityId));
        setStagedImportIdState(stored);
      } catch {
        setStagedImportIdState(null);
      }
    } else {
      setStagedImportIdState(null);
    }
  }, [activityId]);

  const setStagedImportId = (id: string | null) => {
    setStagedImportIdState(id);
    try {
      if (activityId) {
        if (id) {
          localStorage.setItem(STAGED_IMPORT_ID_KEY(activityId), id);
        } else {
          localStorage.removeItem(STAGED_IMPORT_ID_KEY(activityId));
        }
      }
    } catch {
      /* ignore */
    }
  };
  const creditRates = useMemo(
    () => activityData?.activityType?.config?.creditRates ?? [],
    [activityData?.activityType?.config?.creditRates]
  );
  const hasMultipleRoles = uniqBy(creditRates, "participationRole").length > 1;
  const hasMultipleAttendanceTypes = uniqBy(creditRates, "attendanceType").length > 1;

  const extraColumns: ColumnDef<ParticipantRow, unknown>[] = useMemo(
    () => [
      ...(hasMultipleRoles ? [{ accessorKey: "role", header: "Role", size: 300 }] : []),
      ...(hasMultipleAttendanceTypes ? [{ accessorKey: "attendanceType", header: "Attendance Type", size: 150 }] : []),
      { accessorKey: "startDate", header: "Start Date", size: 150 },
      { accessorKey: "endDate", header: "End Date", enableSorting: false, size: 150 },
      { accessorKey: "submittedAt", header: "Submitted", size: 150 },
      { accessorKey: "creditsAwarded", header: "PDR Credits Awarded", size: 150 }
    ],
    [hasMultipleAttendanceTypes, hasMultipleRoles]
  );

  const exportHeaders = [
    "PGA ID",
    "Name",
    "Role",
    "Attendance Type",
    "Start Date",
    "End Date",
    "Submitted",
    "PDR Credits Awarded"
  ];

  const exportRow = (participant: ParticipantRow): (string | number | null | undefined)[] => [
    participant.pgaId,
    participant.name,
    participant.role,
    participant.attendanceType,
    participant.startDate || "",
    participant.endDate || "",
    new Date(participant.submittedAt)
      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      .replace(",", ""),
    participant.creditsAwarded
  ];

  const handleExportCurrentList = async () => {
    if (!activityId) return;

    try {
      const { data } = await exportActivityParticipations({
        variables: {
          activityId
        }
      });
      const downloadUrl = data?.exportActivityParticipations?.url;
      if (!downloadUrl) {
        throw new Error("Download URL not found.");
      }
      window.location.assign(downloadUrl);
    } catch (error) {
      alert("Unable to export participants");
      console.error(error);
    }
  };

  return (
    <>
      <ParticipantsTable
        staticFilters={{ activityId }}
        extraColumns={extraColumns}
        headingBody="View all activity participants below, including participation date and credits awarded."
        exportHeaders={exportHeaders}
        exportRow={exportRow}
        onExportCurrentList={handleExportCurrentList}
        isExportingCurrentList={exportLoading}
        activityTypeId={activityTypeId}
        testId="activity-instance-participants-table"
        noDataCaption="This activity does not have any participants."
        showRoleFilter={hasMultipleRoles}
        showAttendanceTypeFilter={hasMultipleAttendanceTypes}
        canManageParticipationActions={canAddParticipants}
        additionalHeadingActions={[
          {
            label: "Add Participants",
            icon: "add" as const,
            variant: "filled" as const,
            onClick: () => setAddParticipantsModalOpen(true),
            disabled: !canAddParticipants,
            testId: "add-participants-button"
          }
        ]}
      />
      {addParticipantsModalOpen && (
        <AddParticipantsModal
          onDismiss={() => setAddParticipantsModalOpen(false)}
          activityId={activityId}
          creditRates={creditRates}
          activityDuration={activityData?.duration ?? null}
          allowParticipationStarting={activityData?.allowParticipationStarting}
          allowParticipationThrough={activityData?.allowParticipationThrough}
          onSuccess={(processingId) => {
            if (processingId) {
              setStagedImportId(processingId);
            }
            navigate(`/activities/activity-types/${activityTypeId}/activity/${activityId}/add-participants`);
          }}
          stagedImportId={stagedImportId}
          finalizeOnSingleSave
          onSingleSaveSuccess={() => {
            setAddParticipantsModalOpen(false);
            void apolloClient.refetchQueries({ include: [GET_PARTICIPANTS] });
          }}
        />
      )}
    </>
  );
}
